import React, { memo } from 'react'
import ReactGA from 'react-ga4'
import { useCookies } from 'react-cookie'

import ToastService from 'services/toast.service'
import SaveService from 'services/save.service'

import {
  setLocalSortDirectionDescending,
  setLocalSorting,
  setLocalStack,
  setLocalView,
} from 'services/accountSettings.service'

import { useActionless, useActions, useAppSelector } from 'redux/hooks'
import { setDeckStateAction } from 'redux/deck/actions'
import { toggleModal as toggleModalAction } from 'redux/active/actions'
import { selectCardMap, useCanEditDeck } from 'redux/deck/selectors'
import { SET_ACTIVE_STATE, SET_HIGHLIGHT_CARDS } from 'redux/active/actions/types'

import { HUMAN_STACK, HUMAN_SORT, CardType, VIEW } from 'types/deck'

import CollapsibleContentContainer from 'components/elements/CollapsibleContentContainer'
import CardFilters from 'components/filters'
import ChangePriceSource from 'components/deckPage/filtersAndControls/ChangePriceSource'
import QuickAddCard from 'components/cardSearchPanel/searchFormElements/quickAddCard'
import ViewExtras from 'components/deckPage/filtersAndControls/ViewExtras'
import PhatButton from 'components/formElements/PhatButton'
import PhatDropdown from 'components/formElements/PhatDropdown'
import Icon from 'components/elements/Icon'

import styles from './filterBar.module.scss'

const stackOptions = Object.keys(HUMAN_STACK).map(text => ({
  text,
  key: HUMAN_STACK[text],
  value: HUMAN_STACK[text],
}))

const sortOptions = Object.keys(HUMAN_SORT).map(text => ({
  text,
  key: HUMAN_SORT[text],
  value: HUMAN_SORT[text],
}))

const viewOptions = [
  { text: 'Stacks', value: VIEW.CARD },
  { text: 'Text', value: VIEW.TEXT },
  // { text: 'Brewer', value: VIEW.BREWING },
  { text: 'Grid', value: VIEW.GRID },
  // { text: 'Scroll', value: VIEW.SCROLL }, // Commenting out so people stop choosing it, we can remove VIEW.SCROLL later
  { text: 'Table', value: VIEW.SPREADSHEET },
]

const DeckFilters = memo(() => {
  const canEditDeck = useCanEditDeck()
  const cardMap = useAppSelector(selectCardMap)

  const stack = useAppSelector(state => state.deck.stack)
  const sort = useAppSelector(state => state.deck.sorting)
  const sortDirectionDescending = useAppSelector(state => state.deck.sortDirectionDescending)
  const view = useAppSelector(state => state.deck.view)
  const searchOpen = useAppSelector(state => state.active.searchOpen)
  const filteredCards = useAppSelector(state => state.active.filteredCards)

  const [setActiveState] = useActionless(SET_ACTIVE_STATE)
  const [setHighlightCards] = useActionless(SET_HIGHLIGHT_CARDS)
  const [setDeckState, toggleModal] = useActions(setDeckStateAction, toggleModalAction)

  const handleStackChange = (_e: any, { value }: any) => {
    setDeckState({ stack: value })
    setLocalStack(value)
  }

  const handleSortChange = (_e: any, { value }: any) => {
    setDeckState({ sorting: value })
    setLocalSorting(value)
  }
  const handleViewChange = (_e: any, { value }: any) => {
    setDeckState({ view: value })
    setLocalView(value)
  }

  const handleChangeSortDirection = () => {
    setDeckState({ sortDirectionDescending: !sortDirectionDescending })
    setLocalSortDirectionDescending(!sortDirectionDescending)
  }

  const handleQuickAddCard = (card: CardType, matchCheck: 'name' | 'uid' | 'never', incrementOnExists: boolean) => {
    // prettier-ignore
    const matchingCardInDeck = Object.values(cardMap).find(c => {
      if (matchCheck === 'never') return false
      
      
      if (matchCheck === 'name') return c.name === card.name
      if (matchCheck === 'uid') return c.uid === card.uid
    
      return false 
    })

    if (matchingCardInDeck && !incrementOnExists)
      return ToastService.create(
        `${card.name}${
          matchCheck === 'uid' ? ` (${card.setCode})` : ''
        } already is in your deck. You can disable the setting preventing adding duplicate cards via the quick add settings. Change the "Match cards by" setting to "Never" to always add cards from Quick add regardles of whether or not they're already in your deck.`,
        'Add skipped',
        'warning',
      )

    if (matchingCardInDeck) {
      const updatedCard = { ...matchingCardInDeck, qty: matchingCardInDeck.qty + 1 }

      return SaveService.save(updatedCard).then(() =>
        ReactGA.event({ category: 'Deck', action: 'Incremented card from quick add' }),
      )
    }

    const updatedCard = { ...card, qty: card.qty || 1 }

    SaveService.save(updatedCard).then(() => ReactGA.event({ category: 'Deck', action: 'Added card from quick add' }))
  }

  return (
    <CollapsibleContentContainer name="Deck filters" defaultCollapsed={!canEditDeck} className={styles.container}>
      <div className={styles.controls}>
        <div className={styles.addCardSection}>
          <label className={styles.header}>Add card</label>

          <PhatButton color="orange" onClick={() => setActiveState({ searchOpen: !searchOpen })}>
            <Icon name="search" />
            Card search
          </PhatButton>

          {canEditDeck && <QuickAddCard onCardSelected={handleQuickAddCard} />}
        </div>

        <div className={styles.viewsAndSorts}>
          <div>
            <PhatDropdown
              header="View as"
              icon="eye"
              value={view}
              triggerWrapperClassName={styles.dropdowns}
              options={viewOptions.map(opt => ({
                label: opt.text,
                id: `${opt.value}`,
                onClick: () => handleViewChange(null, { value: opt.value }),
              }))}
              extraOptionsElement={<ViewExtras />}
            />
          </div>

          <PhatDropdown
            header="Group by"
            icon="clone outline"
            value={stack}
            triggerWrapperClassName={styles.dropdowns}
            options={stackOptions.map(opt => ({
              label: opt.text,
              id: `${opt.value}`,
              onClick: () => handleStackChange(null, { value: opt.value }),
            }))}
          />

          <PhatDropdown
            header="Sort by"
            icon={sortDirectionDescending ? 'sort amount down' : 'sort amount up'}
            value={sort}
            triggerWrapperClassName={styles.dropdowns}
            options={[
              {
                label: 'Order descending',
                onChange: handleChangeSortDirection,
                type: 'checkbox',
                checked: sortDirectionDescending,
              },
              { type: 'spacer' },
              ...sortOptions.map(opt => ({
                label: opt.text,
                id: `${opt.value}`,
                onClick: () => handleSortChange(null, { value: opt.value }),
              })),
            ]}
          />

          <ChangePriceSource />
        </div>

        <CardFilters
          cardsToFilter={Object.values(cardMap)}
          filteredCards={filteredCards}
          setFilteredCards={setHighlightCards}
        />
      </div>
    </CollapsibleContentContainer>
  )
})

export default DeckFilters
